import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Gauge, Spinner, Empty, Pill, scoreColor } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { Detective, Warning, ThumbsUp, Wrench, MagnifyingGlass, ClockCounterClockwise, ArrowsClockwise, Target, TrendUp, CheckSquare, Square, Rocket, Sparkle, Copy, Scales, ShieldWarning, LinkSimple, FilePdf, Trash, Gavel, EnvelopeSimple, Globe, Plus, ShieldSlash, ArrowSquareOut, Buildings, CaretDown } from "@phosphor-icons/react";

const REMOVAL_ISSUES = [
  { v: "defamation", l: "Defamation / false statements" },
  { v: "inaccurate", l: "Factually inaccurate" },
  { v: "outdated", l: "Outdated / no longer relevant" },
  { v: "privacy", l: "Private / personal info" },
  { v: "harassment", l: "Harassment / abuse" },
  { v: "impersonation", l: "Impersonation" },
  { v: "copyright", l: "Copyright infringement" },
  { v: "other", l: "Other harmful content" },
];
const REMOVAL_RECIPIENTS = [
  { k: "google_deindex", l: "Search de-index (Google/Bing)", icon: Globe },
  { k: "platform_webmaster", l: "Site owner / host", icon: EnvelopeSimple },
  { k: "legal_attorney", l: "Legal / cease-and-desist", icon: Gavel },
];
const REMOVAL_STATUS = ["draft", "sent", "in_progress", "resolved", "rejected"];

const SCORE_ORDER = ["reputation", "authority", "visibility", "trust", "sentiment", "brand_clarity", "seo_readiness", "risk"];
const SCORE_LABELS = {
  reputation: "REPUTATION", authority: "AUTHORITY", visibility: "VISIBILITY", trust: "TRUST",
  sentiment: "SENTIMENT", brand_clarity: "BRAND CLARITY", seo_readiness: "SEO READINESS", risk: "RISK",
};
const riskColor = (v) => (v >= 66 ? "#DC2450" : v >= 33 ? "#9D3FFF" : "#22c55e");
const EVIDENCE_STYLE = {
  fact: "border-emerald-400/40 text-emerald-400", verified: "border-emerald-400/40 text-emerald-400",
  claim: "border-yellow-400/40 text-yellow-400", opinion: "border-accent/40 text-accent",
  allegation: "border-destructive/40 text-destructive", outdated: "border-muted-foreground/40 text-muted-foreground",
  sentiment: "border-secondary/40 text-muted-foreground",
};
const CLAIM_STYLE = {
  SUPPORTED: "border-emerald-400/40 text-emerald-400", PARTIALLY_SUPPORTED: "border-yellow-400/40 text-yellow-400",
  UNVERIFIED: "border-muted-foreground/40 text-muted-foreground", DISPUTED: "border-destructive/40 text-destructive",
  CONTRADICTED: "border-destructive/40 text-destructive", OPINION: "border-accent/40 text-accent",
  OUTDATED: "border-muted-foreground/40 text-muted-foreground",
};

export default function Reputation() {
  const [form, setForm] = useState({ name: "", handle: "", niche: "" });
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [rebuild, setRebuild] = useState(null);
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [pubArticles, setPubArticles] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const justScanned = useRef(false);

  const downloadPdf = async () => {
    if (!report?.id) return;
    setPdfBusy(true);
    try {
      const res = await api.get(`/reputation/reports/${report.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `smi-reputation-${(report.name || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const loadHistory = () => api.get("/reputation/reports").then(({ data }) => setHistory(data)).catch(() => {});
  const loadPub = () => api.get("/reputation/rebuild-articles").then(({ data }) => setPubArticles(data)).catch(() => {});
  const deletePub = async (id) => {
    if (!window.confirm("Delete this article from the web? This removes it from your blog and public profile.")) return;
    try {
      await api.delete(`/reputation/rebuild-articles/${id}`);
      setPubArticles((list) => list.filter((a) => a.id !== id));
      toast.success("Article removed from the web");
    } catch {
      toast.error("Could not delete article");
    }
  };
  useEffect(() => {
    loadHistory();
    loadPub();
    api.get("/profile").then(({ data }) => setForm((f) => ({ name: data.display_name || "", handle: data.handle || "", niche: data.niche || "" }))).catch(() => {});
  }, []);

  useEffect(() => {
    setRebuild(null);
    if (report?.id) {
      api.get(`/reputation/rebuild/${report.id}`).then(({ data }) => setRebuild(data)).catch(() => {
        // No rebuild yet — auto-build one right after a fresh scan
        if (justScanned.current) {
          justScanned.current = false;
          genRebuild(report.id, true);
        }
      });
    }
  }, [report?.id]);

  const scan = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter a name to scan");
    setBusy(true);
    try {
      const { data } = await api.post("/reputation/scan", form);
      justScanned.current = true;
      setReport(data);
      loadHistory();
      toast.success("Reputation audit complete — building your AI rebuild plan…");
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (status === 402) toast.error(msg, { action: { label: "Upgrade", onClick: () => (window.location.href = "/billing") } });
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const genRebuild = async (reportId = report?.id, auto = false) => {
    if (!reportId) return;
    setRebuildBusy(true);
    try {
      const { data } = await api.post("/reputation/rebuild", { report_id: reportId });
      setRebuild(data);
      toast.success(auto ? "AI rebuild plan ready" : "Reputation rebuild campaign created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRebuildBusy(false);
    }
  };

  const toggleTask = async (taskId, done) => {
    try {
      const { data } = await api.patch(`/reputation/rebuild/${rebuild.id}/task/${taskId}`, { done });
      setRebuild(data);
    } catch (err) {
      toast.error("Could not update task");
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How Reputation Scan & Rebuild works" steps={[
        "Enter a name/handle/niche and click Run reputation scan for a full web audit.",
        "Review the score, findings and corrective measures, then click Build rebuild plan.",
        "Tick off rebuild tasks to raise your projected score, or Generate a post from any task.",
        "Click 'Publish to Web & Media' to auto-publish positive SEO articles about the subject.",
      ]} />

      {/* Published to the web — always visible so you can see live rebuild content */}
      <Card testid="published-rebuild-panel">
        <div className="flex items-center gap-2 mb-1">
          <Rocket size={18} weight="fill" className="text-primary" />
          <h3 className="font-head font-bold text-lg">Published to Web &amp; Media</h3>
          <span className="font-mono text-sm text-muted-foreground ml-auto">{pubArticles.length} live</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Positive SEO articles pushed to the web from your reputation rebuilds. These rank in search and appear on your public profile.</p>
        {pubArticles.length === 0 ? (
          <Empty>No rebuild articles published yet. Run a scan, build a rebuild plan, then click "Publish to Web &amp; Media".</Empty>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {pubArticles.map((a) => (
              <div key={a.id} data-testid={`pub-article-${a.id}`}
                className="border border-border p-3 hover:border-primary transition-colors duration-200 group relative">
                <button onClick={() => deletePub(a.id)} data-testid={`delete-pub-${a.id}`} title="Delete from the web"
                  className="absolute top-2 right-2 p-1.5 bg-black/60 border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors duration-200 z-10">
                  <Trash size={14} weight="bold" />
                </button>
                <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="block pr-8">
                  <div className="text-sm font-medium group-hover:text-primary transition-colors duration-200 line-clamp-2">{a.title}</div>
                  <div className="overline text-muted-foreground mt-2 flex items-center gap-2">
                    <span className="text-emerald-400">● LIVE</span>
                    <span>{(a.published_at || "").slice(0, 10)}</span>
                    <span className="ml-auto">{a.views || 0} views</span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        )}
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scan form */}
        <Card className="lg:col-span-1 h-fit" testid="scan-form-card">
          <div className="flex items-center gap-2 mb-4">
            <Detective size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">Deep Web Scan</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            SMI searches the web & SEO landscape for all data on the subject and returns a detailed audit with corrective measures.
          </p>
          <form onSubmit={scan} className="space-y-4">
            <Input label="Name / Public figure" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jordan Vega" testid="scan-name" />
            <Input label="Primary handle" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} placeholder="@jordanvega" testid="scan-handle" />
            <Input label="Niche / Industry" value={form.niche} onChange={(v) => setForm({ ...form, niche: v })} placeholder="Fashion & lifestyle" testid="scan-niche" />
            <button
              type="submit"
              disabled={busy}
              data-testid="run-scan-btn"
              className="w-full bg-primary text-primary-foreground font-semibold font-semibold py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50"
            >
              {busy ? <Spinner label="Scanning the web…" /> : (<><MagnifyingGlass size={18} weight="bold" /> Run reputation scan</>)}
            </button>
          </form>

          {history.length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <Overline className="flex items-center gap-1 mb-3"><ClockCounterClockwise size={12} /> RECENT AUDITS</Overline>
              <div className="space-y-1">
                {history.slice(0, 6).map((h) => (
                  <button
                    key={h.id}
                    data-testid={`history-${h.id}`}
                    onClick={() => setReport(h)}
                    className="w-full text-left px-2 py-2 text-sm hover:bg-secondary transition-colors duration-200 flex items-center justify-between"
                  >
                    <span className="truncate">{h.name}</span>
                    <span className="font-mono font-bold" style={{ color: scoreColor(h.reputation_score) }}>{h.reputation_score}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Report */}
        <div className="lg:col-span-2 space-y-4">
          {!report && !busy && (
            <Empty>Run a scan to generate a full reputation intelligence report.</Empty>
          )}
          {busy && !report && (
            <Card><div className="py-16 flex justify-center"><Spinner label="Aggregating web mentions, news, forums & SEO signals…" /></div></Card>
          )}
          {report && (
            <>
              <Card testid="report-header">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <Gauge score={report.reputation_score} size={160} />
                  <div className="flex-1">
                    <Overline className="text-accent">EXECUTIVE SUMMARY · {report.name}</Overline>
                    <p className="text-sm mt-2 leading-relaxed">{report.summary}</p>
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {report.web_data_retrieved && <Pill tone="positive" data-testid="live-web-pill">● Live web data</Pill>}
                      <Pill tone="positive">{report.sentiment?.positive}% positive</Pill>
                      <Pill tone="neutral">{report.sentiment?.neutral}% neutral</Pill>
                      <Pill tone="negative">{report.sentiment?.negative}% negative</Pill>
                    </div>
                    <div className="flex gap-2 mt-5 flex-wrap">
                      <button onClick={downloadPdf} disabled={pdfBusy} data-testid="download-pdf-btn"
                        className="border border-border px-4 py-2 text-sm flex items-center gap-2 hover:border-primary hover:text-primary transition-colors duration-200 disabled:opacity-50">
                        {pdfBusy ? <Spinner label="Preparing…" /> : (<><FilePdf size={16} weight="bold" /> Download PDF report</>)}
                      </button>
                      {!rebuild && (
                        <button onClick={() => genRebuild()} disabled={rebuildBusy} data-testid="report-rebuild-btn"
                          className="bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
                          {rebuildBusy ? <Spinner label="Building…" /> : (<><Rocket size={16} weight="fill" /> Build Rebuild Plan</>)}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              {report.scores && (
                <Card testid="scorecard">
                  <Overline className="mb-3 flex items-center gap-1"><Scales size={12} weight="fill" /> SMI SCORECARD · EXPLAINABLE</Overline>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {SCORE_ORDER.map((key) => {
                      const s = report.scores[key];
                      if (!s) return null;
                      const isRisk = key === "risk";
                      const val = typeof s === "object" ? s.value : s;
                      const color = isRisk ? riskColor(val) : scoreColor(val);
                      return (
                        <div key={key} className="border border-border p-3" data-testid={`score-${key}`}>
                          <div className="overline text-muted-foreground">{SCORE_LABELS[key]}{isRisk ? " · lower better" : ""}</div>
                          <div className="font-head font-bold text-3xl tracking-tighter mt-1" style={{ color }}>{val}</div>
                          {s.explanation && <div className="text-[0.65rem] text-muted-foreground mt-1 leading-snug">{s.explanation}</div>}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {report.requires_review && (
                <div className="border border-destructive/50 bg-destructive/10 p-4 flex items-start gap-3" data-testid="review-gate-banner">
                  <ShieldWarning size={24} weight="fill" className="text-destructive shrink-0" />
                  <div>
                    <div className="font-semibold text-destructive">High-risk findings — human review required</div>
                    <p className="text-sm text-muted-foreground mt-1">SMI will not auto-publish content about this subject until you review the sensitive findings below. This protects you from amplifying unverified allegations.</p>
                    {report.risk_flags?.filter((f) => f.requires_human_review || f.severity === "high").map((f, i) => (
                      <div key={i} className="text-xs mt-2 flex items-center gap-2" data-testid={`risk-flag-${i}`}>
                        <Pill tone="high">{f.severity}</Pill>
                        <span className="text-muted-foreground">{f.category}: {f.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Card testid="web-presence">
                <Overline className="mb-3">WEB PRESENCE · CLASSIFIED FINDINGS</Overline>
                <div className="divide-y divide-border">
                  {report.web_presence?.map((w, i) => (
                    <div key={i} className="py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium">{w.source}</span>
                        {w.evidence_type && (
                          <span className={`overline px-1.5 py-0.5 border ${EVIDENCE_STYLE[w.evidence_type] || "border-border text-muted-foreground"}`} data-testid={`evidence-${i}`}>{w.evidence_type}</span>
                        )}
                        <Pill tone={w.sentiment}>{w.sentiment}</Pill>
                        <span className="overline text-muted-foreground">{w.type} · reach {w.reach}</span>
                        {typeof w.source_quality === "number" && (
                          <span className="overline text-muted-foreground ml-auto">source quality {Math.round(w.source_quality * 100)}%</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{w.snippet}</p>
                      {w.source_url && (
                        <a href={w.source_url} target="_blank" rel="noreferrer" data-testid={`source-link-${i}`}
                          className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline break-all">
                          <LinkSimple size={12} /> {w.source_url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {report.claims?.length > 0 && (
                <Card testid="claims">
                  <Overline className="mb-3 flex items-center gap-1"><Scales size={12} weight="fill" /> CLAIMS · VERIFICATION STATUS</Overline>
                  <div className="space-y-2">
                    {report.claims.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm" data-testid={`claim-${i}`}>
                        <span className={`overline px-1.5 py-0.5 border shrink-0 ${CLAIM_STYLE[c.status] || "border-border text-muted-foreground"}`}>{(c.status || "").replace(/_/g, " ")}</span>
                        <div className="flex-1">
                          <span className="text-muted-foreground">{c.claim}</span>
                          {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-primary ml-1 hover:underline">source ↗</a>}
                        </div>
                        <span className="font-mono text-xs text-muted-foreground shrink-0">{Math.round((c.confidence || 0) * 100)}%</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <Card testid="negative-findings">
                  <Overline className="flex items-center gap-1 mb-3 text-destructive"><Warning size={12} weight="fill" /> RISKS & NEGATIVE NARRATIVES</Overline>
                  <ul className="space-y-2">
                    {report.negative_findings?.map((n, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-destructive">▪</span>{n}</li>
                    ))}
                  </ul>
                </Card>
                <Card testid="positive-signals">
                  <Overline className="flex items-center gap-1 mb-3 text-emerald-400"><ThumbsUp size={12} weight="fill" /> POSITIVE SIGNALS</Overline>
                  <ul className="space-y-2">
                    {report.positive_signals?.map((n, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-emerald-400">▪</span>{n}</li>
                    ))}
                  </ul>
                </Card>
              </div>

              <Card testid="corrective-measures">
                <Overline className="flex items-center gap-1 mb-4"><Wrench size={12} weight="fill" /> CORRECTIVE MEASURES · ACTION PLAN</Overline>
                <div className="space-y-3">
                  {report.corrective_measures?.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 border border-border p-3">
                      <span className="font-mono text-sm text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Pill tone={c.priority}>{c.priority} priority</Pill>
                        </div>
                        <div className="text-sm font-medium">{c.action}</div>
                        <div className="text-xs text-muted-foreground mt-1">Impact: {c.impact}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card testid="seo-keywords">
                <Overline className="mb-3">ENTITY &amp; AUTHORITY KEYWORDS</Overline>
                <div className="flex flex-wrap gap-2">
                  {report.seo_keywords?.map((k, i) => (
                    <span key={i} className="font-mono text-xs px-2 py-1 border border-border bg-secondary text-muted-foreground">{k}</span>
                  ))}
                </div>
              </Card>

              <RemovalTool report={report} />

              <BarnacleTool report={report} />

              {/* Reputation Rebuild */}
              {!rebuild ? (
                <div className="relative p-[1px] tracing-border">
                  <Card className="bg-card" testid="rebuild-cta">
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <Rocket size={36} weight="fill" className="text-primary" />
                      <div className="flex-1 text-center sm:text-left">
                        <h3 className="font-head font-bold text-lg">Rebuild {report.name}'s reputation</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Turn this assessment into a phased, trackable recovery campaign that lifts the score toward a healthy target.
                        </p>
                      </div>
                      <button
                        onClick={() => genRebuild()}
                        disabled={rebuildBusy}
                        data-testid="generate-rebuild-btn"
                        className="bg-primary text-primary-foreground font-semibold font-semibold px-5 py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 whitespace-nowrap"
                      >
                        {rebuildBusy ? <Spinner label="Building plan…" /> : (<><ArrowsClockwise size={18} weight="bold" /> Build rebuild plan</>)}
                      </button>
                    </div>
                  </Card>
                </div>
              ) : (
                <RebuildCampaign rebuild={rebuild} report={report} onToggle={toggleTask} onPushed={loadPub} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RebuildCampaign({ rebuild, report, onToggle, onPushed }) {
  const current = rebuild.projected_score ?? rebuild.baseline_score;
  const [genId, setGenId] = useState(null);
  const [posts, setPosts] = useState({});
  const [pushBusy, setPushBusy] = useState(false);
  const [seoCount, setSeoCount] = useState(rebuild.seo_article_count || 0);
  const [autoFlood, setAutoFlood] = useState(false);
  const [supBusy, setSupBusy] = useState(false);
  const negatives = report?.negative_findings || [];

  const suppress = async (confirmHighRisk = false) => {
    setSupBusy(true);
    try {
      const url = `/reputation/rebuild/${rebuild.id}/suppress${confirmHighRisk ? "?confirm_high_risk=true" : ""}`;
      const { data } = await api.post(url);
      setSeoCount(data.total_seo_articles);
      setAutoFlood(true);
      onPushed && onPushed();
      toast.success(`Suppressing ${data.negatives_targeted} negative(s) — ${data.published} counter-articles published + 24/7 auto-flood ON`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.code === "requires_human_review") {
        const reasons = (detail.reasons || []).join("\n• ");
        if (window.confirm(`⚠ HUMAN REVIEW REQUIRED\n\n${detail.message}\n\n• ${reasons}\n\nOnly proceed if you have verified these findings. Publish suppression content anyway?`)) {
          setSupBusy(false);
          return suppress(true);
        }
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally {
      setSupBusy(false);
    }
  };

  useEffect(() => {
    api.get("/reputation/autoflood").then(({ data }) => setAutoFlood(data.enabled)).catch(() => {});
  }, []);

  const toggleFlood = async () => {
    const next = !autoFlood;
    setAutoFlood(next);
    try {
      await api.post("/reputation/autoflood", { enabled: next });
      toast.success(next ? "24/7 auto-flood ON — the AI engine will keep publishing about you" : "Auto-flood paused");
    } catch {
      setAutoFlood(!next);
      toast.error("Could not update auto-flood");
    }
  };

  const pushSeo = async (confirmHighRisk = false) => {
    setPushBusy(true);
    try {
      const url = `/reputation/rebuild/${rebuild.id}/push-seo${confirmHighRisk ? "?confirm_high_risk=true" : ""}`;
      const { data } = await api.post(url);
      setSeoCount(data.total_seo_articles);
      onPushed && onPushed();
      toast.success(`${data.published} positive articles published to the web`, { action: { label: "View blog", onClick: () => window.open("/blog", "_blank") } });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.code === "requires_human_review") {
        const reasons = (detail.reasons || []).join("\n• ");
        const ok = window.confirm(
          `⚠ HUMAN REVIEW REQUIRED\n\n${detail.message}\n\n• ${reasons}\n\nOnly proceed if you have verified these findings and confirm it is safe & accurate to publish. Publish anyway?`
        );
        if (ok) { setPushBusy(false); return pushSeo(true); }
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally {
      setPushBusy(false);
    }
  };

  const genPost = async (taskId) => {
    setGenId(taskId);
    try {
      const { data } = await api.post(`/reputation/rebuild/${rebuild.id}/task/${taskId}/content`);
      setPosts((p) => ({ ...p, [taskId]: data }));
      toast.success("Post generated & saved to Content library");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenId(null);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <Card testid="rebuild-campaign">
      <div className="flex items-center gap-2 mb-4">
        <Rocket size={22} weight="fill" className="text-primary" />
        <h3 className="font-head font-bold text-lg">AI Reputation Rebuild Campaign</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{rebuild.strategy_summary}</p>

      {/* Push to the web */}
      <div className="border border-primary/30 bg-primary/5 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="push-seo-panel">
        <Rocket size={24} weight="fill" className="text-primary shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-medium">Publish this campaign to the web &amp; media</div>
          <div className="text-xs text-muted-foreground">
            Auto-writes &amp; publishes positive, high-authority SEO articles about {rebuild.subject} to your public blog, profile and media outlets.
            {seoCount > 0 && <span className="text-emerald-400"> · {seoCount} live</span>}
          </div>
        </div>
        <button onClick={() => pushSeo()} disabled={pushBusy} data-testid="push-seo-btn"
          className="bg-primary text-primary-foreground font-semibold px-4 py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 whitespace-nowrap">
          {pushBusy ? <Spinner label="Publishing…" /> : (<><Rocket size={16} weight="fill" /> Publish to Web &amp; Media</>)}
        </button>
      </div>

      {/* Auto-flood 24/7 toggle */}
      <div className="border border-border p-4 mb-6 flex items-center gap-3" data-testid="autoflood-panel">
        <button type="button" onClick={toggleFlood} data-testid="autoflood-toggle"
          className={`relative h-5 w-9 rounded-full transition-colors duration-200 shrink-0 ${autoFlood ? "bg-primary" : "bg-secondary border border-border"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all duration-200 ${autoFlood ? "left-4" : "left-0.5"}`} />
        </button>
        <div className="flex-1">
          <div className="text-sm font-medium flex items-center gap-2">
            Auto-flood the web 24/7 {autoFlood && <span className="text-emerald-400 text-xs">● ACTIVE</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            When ON, SMI's AI engine keeps writing &amp; publishing fresh positive articles about {rebuild.subject} around the clock — no clicks needed.
          </div>
        </div>
      </div>

      {/* Suppression tracker */}
      {(() => {
        const perNeg = 3;
        const negCount = Math.max(negatives.length, 1);
        const needed = negCount * perNeg;
        const coverage = Math.min(100, Math.round((seoCount / needed) * 100));
        const outranked = Math.min(negatives.length, Math.floor(seoCount / perNeg));
        const barColor = coverage >= 80 ? "#22c55e" : coverage >= 40 ? "#E9AC0C" : "#DC2450";
        return (
          <div className="border border-border p-4 mb-6" data-testid="suppression-tracker">
            <div className="flex items-center gap-2 mb-3">
              <Target size={18} weight="bold" className="text-primary" />
              <h4 className="font-head font-bold">Suppression Tracker</h4>
              <span className="font-mono text-xs text-muted-foreground ml-auto" data-testid="suppression-coverage">{coverage}% page-one coverage</span>
            </div>
            <div className="h-2 bg-secondary overflow-hidden mb-3">
              <div className="h-full transition-all duration-500" style={{ width: `${coverage}%`, background: barColor }} />
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="font-head text-2xl font-bold text-primary" data-testid="tracker-outranked">{outranked}/{negatives.length}</div>
                <div className="overline text-muted-foreground">NEGATIVES OUTRANKED</div>
              </div>
              <div>
                <div className="font-head text-2xl font-bold" data-testid="tracker-assets">{seoCount}</div>
                <div className="overline text-muted-foreground">POSITIVE ASSETS LIVE</div>
              </div>
              <div>
                <div className={`font-head text-2xl font-bold ${autoFlood ? "text-emerald-400" : "text-muted-foreground"}`}>{autoFlood ? "ON" : "OFF"}</div>
                <div className="overline text-muted-foreground">24/7 AUTO-FLOOD</div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Estimated from authoritative positive assets published vs. negatives detected (~{perNeg} per negative to push it off page one). Connect Google Search Console later for exact live rankings.</p>
          </div>
        );
      })()}

      {/* Negative suppression engine */}
      <div className="border border-destructive/30 bg-destructive/5 p-4 mb-6" data-testid="suppress-panel">
        <div className="flex items-center gap-2 mb-2">
          <ShieldWarning size={20} weight="fill" className="text-primary" />
          <h4 className="font-head font-bold">Suppress Negative Results</h4>
          <span className="font-mono text-xs text-muted-foreground ml-auto">{negatives.length} negatives detected</span>
        </div>
        {negatives.length > 0 && (
          <ul className="space-y-1 mb-3">
            {negatives.map((n, i) => (
              <li key={i} className="text-xs text-muted-foreground flex gap-2" data-testid={`suppress-negative-${i}`}>
                <span className="text-destructive shrink-0">▼</span>{n}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
          SMI publishes a targeted, authoritative positive article for each negative to <strong>outrank and bury it</strong> in search, and keeps flooding 24/7. Note: we can't delete pages on sites we don't own — for defamatory or illegal content, use the platform's removal/legal process.
        </p>
        <button onClick={() => suppress()} disabled={supBusy} data-testid="suppress-btn"
          className="w-full bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
          {supBusy ? <Spinner label="Deploying suppression content…" /> : (<><ShieldWarning size={16} weight="fill" /> Suppress all negatives now</>)}
        </button>
      </div>

      {/* Score journey + progress */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="border border-border p-3 text-center" data-testid="rebuild-baseline">
          <Overline className="text-muted-foreground">BASELINE</Overline>
          <div className="font-mono text-2xl font-bold mt-1" style={{ color: scoreColor(rebuild.baseline_score) }}>{rebuild.baseline_score}</div>
        </div>
        <div className="border border-primary/40 bg-primary/5 p-3 text-center" data-testid="rebuild-projected">
          <Overline className="text-primary">PROJECTED NOW</Overline>
          <div className="font-mono text-2xl font-bold mt-1 flex items-center justify-center gap-1" style={{ color: scoreColor(current) }}>
            <TrendUp size={18} weight="bold" /> {current}
          </div>
        </div>
        <div className="border border-border p-3 text-center" data-testid="rebuild-target">
          <Overline className="text-muted-foreground flex items-center justify-center gap-1"><Target size={11} weight="bold" /> TARGET</Overline>
          <div className="font-mono text-2xl font-bold mt-1" style={{ color: scoreColor(rebuild.target_score) }}>{rebuild.target_score}</div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <Overline>CAMPAIGN PROGRESS · {rebuild.completed_tasks}/{rebuild.total_tasks} tasks</Overline>
          <span className="font-mono text-sm text-primary">{rebuild.progress}% · ~{rebuild.estimated_weeks} wks</span>
        </div>
        <div className="h-2 bg-secondary overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${rebuild.progress}%` }} data-testid="rebuild-progress-bar" />
        </div>
      </div>

      {/* Phases */}
      <div className="space-y-5">
        {rebuild.phases.map((ph, pi) => (
          <div key={pi} data-testid={`rebuild-phase-${pi}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-head font-bold text-sm">{ph.name}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{ph.goal}</p>
            <div className="space-y-2">
              {ph.tasks.map((t) => (
                <div key={t.id} data-testid={`rebuild-task-${t.id}`} className="border border-border">
                  <div className="flex items-start gap-3 p-3">
                    <button onClick={() => onToggle(t.id, !t.done)} data-testid={`rebuild-toggle-${t.id}`} className="shrink-0 mt-0.5">
                      {t.done
                        ? <CheckSquare size={20} weight="fill" className="text-primary" />
                        : <Square size={20} weight="bold" className="text-muted-foreground" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Pill>{t.channel}</Pill>
                        <Pill tone={t.impact}>{t.impact} impact</Pill>
                      </div>
                    </div>
                    <button
                      onClick={() => genPost(t.id)}
                      disabled={genId === t.id}
                      data-testid={`gen-post-${t.id}`}
                      className="shrink-0 border border-border px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 hover:border-primary hover:text-primary transition-colors duration-200 disabled:opacity-50"
                    >
                      {genId === t.id ? <Spinner label="…" /> : (<><Sparkle size={14} weight="fill" /> Generate post</>)}
                    </button>
                  </div>
                  {posts[t.id] && (
                    <div className="border-t border-border bg-secondary/40 p-3" data-testid={`gen-post-result-${t.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Overline className="text-primary">READY-TO-POST · {posts[t.id].platform}</Overline>
                        <button onClick={() => copy(posts[t.id].body)} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Copy size={14} weight="bold" /></button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{posts[t.id].body}</p>
                      {posts[t.id].hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {posts[t.id].hashtags.map((h, j) => (
                            <span key={j} className="font-mono text-xs text-primary">{h.startsWith("#") ? h : `#${h}`}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BarnacleTool({ report }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});

  const load = () => api.get("/reputation/barnacle").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reputation/barnacle", { report_id: report?.id, subject: report?.name, niche: report?.niche });
      setItems((prev) => [...data.properties, ...prev]);
      toast.success(`${data.properties.length} name-optimized profiles generated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    try { await api.patch(`/reputation/barnacle/${id}`, { status }); } catch { load(); }
  };
  const remove = async (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    try { await api.delete(`/reputation/barnacle/${id}`); } catch { load(); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const liveCount = items.filter((p) => p.status === "live").length;

  return (
    <Card testid="barnacle-tool">
      <div className="flex items-center gap-2 mb-2">
        <Buildings size={22} weight="fill" className="text-primary" />
        <h3 className="font-head font-bold text-lg">Own Page One — Profile Network</h3>
        {items.length > 0 && <span className="font-mono text-xs text-muted-foreground ml-auto">{liveCount}/{items.length} live</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        Barnacle SEO: create a portfolio of name-optimized profiles on high-authority sites (Medium, About.me, Crunchbase, LinkedIn, YouTube…) that reliably rank on page one for {report?.name || "the subject"}'s name — pushing negatives down and controlling the first impression.
      </p>
      <button onClick={generate} disabled={busy} data-testid="barnacle-generate-btn"
        className="w-full bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 mb-5">
        {busy ? <Spinner label="Designing profile network…" /> : (<><Sparkle size={16} weight="fill" /> {items.length > 0 ? "Regenerate profile network" : "Generate profile network"}</>)}
      </button>
      {items.length === 0 ? (
        <Empty>No owned properties yet. Generate a page-one profile network to blanket search results with your own content.</Empty>
      ) : (
        <div className="space-y-3" data-testid="barnacle-list">
          {items.map((p) => (
            <div key={p.id} className="border border-border" data-testid={`barnacle-item-${p.id}`}>
              <div className="flex items-start gap-3 p-3">
                <button onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <Globe size={15} weight="bold" className="text-primary shrink-0" />
                    <span className="text-sm font-semibold truncate">{p.platform}</span>
                    <CaretDown size={14} className={`text-muted-foreground transition-transform duration-200 ${open[p.id] ? "rotate-180" : ""}`} />
                  </div>
                  <div className="font-mono text-xs text-muted-foreground truncate mt-0.5">{p.url_pattern}</div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setStatus(p.id, p.status === "live" ? "planned" : "live")} data-testid={`barnacle-status-${p.id}`}
                    className={`px-2.5 py-1.5 text-xs font-medium border transition-colors duration-200 ${p.status === "live" ? "border-emerald-400/50 text-emerald-400 bg-emerald-400/10" : "border-border text-muted-foreground"}`}>
                    {p.status === "live" ? "● Live" : "Mark live"}
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`barnacle-delete-${p.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash size={15} weight="bold" /></button>
                </div>
              </div>
              {open[p.id] && (
                <div className="border-t border-border p-3 space-y-3" data-testid={`barnacle-detail-${p.id}`}>
                  {p.why && <div className="text-xs text-muted-foreground"><span className="text-primary font-medium">Why it ranks:</span> {p.why}</div>}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Overline className="text-primary">OPTIMIZED BIO</Overline>
                      <button onClick={() => copy(p.bio)} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Copy size={14} weight="bold" /></button>
                    </div>
                    {p.headline && <div className="text-sm font-semibold mb-1">{p.headline}</div>}
                    <p className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{p.bio}</p>
                  </div>
                  {p.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.keywords.map((k, i) => <span key={i} className="font-mono text-xs px-2 py-1 border border-border bg-secondary text-muted-foreground">{k}</span>)}
                    </div>
                  )}
                  {p.setup_steps?.length > 0 && (
                    <div>
                      <Overline className="mb-1">SETUP STEPS</Overline>
                      <ol className="space-y-1">
                        {p.setup_steps.map((s, i) => (
                          <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                            <span className="shrink-0 h-5 w-5 flex items-center justify-center bg-primary/15 border border-primary/30 text-primary font-mono text-xs">{i + 1}</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RemovalTool({ report }) {
  const negativePages = (report?.web_presence || []).filter(
    (w) => w.sentiment === "negative" || w.evidence_type === "allegation"
  );
  const [form, setForm] = useState({ target_url: "", page_title: "", issue_type: "defamation", description: "" });
  const [recips, setRecips] = useState({ google_deindex: true, platform_webmaster: true, legal_attorney: true });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({});

  const load = () => api.get("/reputation/removals").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const prefill = (w) => {
    setForm({
      target_url: w.source_url || "",
      page_title: w.source || "",
      issue_type: w.evidence_type === "allegation" ? "defamation" : "inaccurate",
      description: w.snippet || "",
    });
    document.getElementById("removal-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const toggleRecip = (k) => setRecips((r) => ({ ...r, [k]: !r[k] }));

  const draft = async () => {
    const selected = Object.keys(recips).filter((k) => recips[k]);
    if (selected.length === 0) { toast.error("Select at least one recipient"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reputation/removal/draft", {
        report_id: report?.id, ...form, recipients: selected,
      });
      setItems((prev) => [data, ...prev]);
      setOpen((o) => ({ ...o, [data.id]: true }));
      setForm({ target_url: "", page_title: "", issue_type: "defamation", description: "" });
      toast.success("Removal letters drafted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try { await api.patch(`/reputation/removals/${id}`, { status }); }
    catch { toast.error("Could not update status"); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this removal request?")) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
    try { await api.delete(`/reputation/removals/${id}`); }
    catch { toast.error("Could not delete"); load(); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };

  return (
    <Card testid="removal-tool">
      <div className="flex items-center gap-2 mb-2">
        <ShieldSlash size={22} weight="fill" className="text-primary" />
        <h3 className="font-head font-bold text-lg">Content Removal &amp; Takedown</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        Draft formal removal requests for defamatory, false or harmful pages — for search-engine de-indexing (Google/Bing),
        the site owner/host, and a reviewable legal cease-and-desist template. Removal is at each recipient's discretion; this is not legal advice.
      </p>

      {/* One-click from detected negatives */}
      {negativePages.length > 0 && (
        <div className="mb-5" data-testid="removal-detected">
          <Overline className="text-destructive mb-2 flex items-center gap-1"><Warning size={12} weight="fill" /> DETECTED NEGATIVE PAGES</Overline>
          <div className="space-y-2">
            {negativePages.map((w, i) => (
              <div key={i} className="flex items-start gap-3 border border-destructive/30 bg-destructive/5 p-3" data-testid={`removal-detected-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{w.source}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{w.snippet}</div>
                  {w.source_url && (
                    <a href={w.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline">
                      <LinkSimple size={12} weight="bold" /> {w.source_url.slice(0, 48)}
                    </a>
                  )}
                </div>
                <button onClick={() => prefill(w)} data-testid={`removal-draft-detected-${i}`}
                  className="shrink-0 border border-border px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 hover:border-primary hover:text-primary transition-colors duration-200">
                  Draft removal
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual / prefilled form */}
      <div id="removal-form" className="border border-border p-4 mb-6 space-y-3" data-testid="removal-form">
        <Overline>PAGE TO REMOVE</Overline>
        <input value={form.target_url} onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))}
          placeholder="https://example.com/harmful-page" data-testid="removal-url-input"
          className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.page_title} onChange={(e) => setForm((f) => ({ ...f, page_title: e.target.value }))}
            placeholder="Page title / source (optional)" data-testid="removal-title-input"
            className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
          <select value={form.issue_type} onChange={(e) => setForm((f) => ({ ...f, issue_type: e.target.value }))}
            data-testid="removal-issue-select"
            className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200">
            {REMOVAL_ISSUES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Describe what's false/harmful and why it should be removed (optional but improves the letter)"
          rows={3} data-testid="removal-desc-input"
          className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 resize-none" />
        <div>
          <Overline className="mb-2">SEND TO</Overline>
          <div className="flex flex-wrap gap-2">
            {REMOVAL_RECIPIENTS.map(({ k, l, icon: Icon }) => (
              <button key={k} type="button" onClick={() => toggleRecip(k)} data-testid={`removal-recip-${k}`}
                className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-medium transition-colors duration-200 ${recips[k] ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
                <Icon size={14} weight="bold" /> {l}
              </button>
            ))}
          </div>
        </div>
        <button onClick={draft} disabled={busy || !form.target_url} data-testid="removal-draft-btn"
          className="w-full bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
          {busy ? <Spinner label="Drafting letters…" /> : (<><Sparkle size={16} weight="fill" /> Draft removal letters</>)}
        </button>
      </div>

      {/* Drafted requests */}
      {items.length === 0 ? (
        <Empty>No removal requests yet. Enter a URL above or draft one from a detected negative page.</Empty>
      ) : (
        <div className="space-y-3" data-testid="removal-list">
          {items.map((it) => (
            <div key={it.id} className="border border-border" data-testid={`removal-item-${it.id}`}>
              <div className="flex items-start gap-3 p-3">
                <button onClick={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))} className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium truncate">{it.page_title || it.target_url || "Removal request"}</div>
                  <div className="text-xs text-muted-foreground truncate">{it.target_url}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Pill>{it.issue_type}</Pill>
                    <span className="font-mono text-[11px] text-muted-foreground">{Object.keys(it.recipients || {}).length} letters</span>
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} data-testid={`removal-status-${it.id}`}
                    className="bg-secondary border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-primary capitalize">
                    {REMOVAL_STATUS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                  <button onClick={() => remove(it.id)} data-testid={`removal-delete-${it.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
              {open[it.id] && (
                <div className="border-t border-border p-3 space-y-4" data-testid={`removal-letters-${it.id}`}>
                  {Object.entries(it.recipients || {}).map(([k, r]) => (
                    <div key={k} className="border border-border bg-secondary/30 p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Overline className="text-primary">{r.title}</Overline>
                        <button onClick={() => copy(`${r.subject_line}\n\n${r.body}`)} data-testid={`removal-copy-${it.id}-${k}`}
                          className="text-muted-foreground hover:text-primary transition-colors duration-200"><Copy size={14} weight="bold" /></button>
                      </div>
                      {r.subject_line && <div className="text-sm font-semibold mb-2">Subject: {r.subject_line}</div>}
                      <p className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{r.body}</p>
                      {r.action_links?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {r.action_links.map((a, j) => (
                            <a key={j} href={a.url} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 border border-border px-2.5 py-1.5 text-xs font-medium hover:border-primary hover:text-primary transition-colors duration-200">
                              <ArrowSquareOut size={12} weight="bold" /> {a.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <input
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200"
      />
    </div>
  );
}
